import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, BadgeCheck, XCircle } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const VerificationPage = () => {
  const { digitalId } = useParams();
  const [rec, setRec] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    api.get(`/verification/${digitalId}`)
      .then((r) => { setRec(r.data); setState("ok"); })
      .catch(() => setState("notfound"));
  }, [digitalId]);

  return (
    <div className="bg-ivory min-h-[70vh]">
      <Seo title={`Vérification ${digitalId}`} description="Vérification d'authenticité d'un achat numérique." path={`/verification/${digitalId}`} noindex />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {state === "loading" && <p className="text-center text-brown-black/50">Vérification en cours…</p>}

        {state === "notfound" && (
          <div className="text-center bg-white rounded-2xl border border-red-200 p-10" data-testid="verification-notfound">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 flex items-center justify-center mb-5"><XCircle size={36} className="text-red-500" /></div>
            <h1 className="font-heading text-2xl text-brown-black">Identité digitale introuvable</h1>
            <p className="text-brown-black/60 mt-2">Cet identifiant ne correspond à aucun achat authentifié.</p>
            <Link to="/boutique" className="inline-block mt-6 text-gold font-semibold hover:underline">Aller à la boutique</Link>
          </div>
        )}

        {state === "ok" && rec && (
          <div className="bg-white rounded-2xl border border-gold/25 shadow-xl overflow-hidden" data-testid="verification-card">
            <div className="bg-gradient-to-br from-[#0b1436] to-[#080b18] text-ivory px-8 py-8 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-3">
                <BadgeCheck size={38} className="text-emerald-400" />
              </div>
              <p className="text-sm uppercase tracking-[0.2em] text-gold-light font-semibold">Achat authentifié</p>
              <p className="font-heading text-3xl mt-1" data-testid="verification-status">ACHAT AUTHENTIFIÉ</p>
            </div>
            <div className="p-8 space-y-5">
              <div>
                <p className="text-xs uppercase tracking-[0.12em] text-brown-dark/50 font-semibold">Identité digitale</p>
                <p className="font-heading text-2xl font-semibold text-gold tracking-wide mt-1" data-testid="verification-id">{rec.digital_id}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.12em] text-brown-dark/50 font-semibold">Produit{rec.products.length > 1 ? "s" : ""}</p>
                <ul className="mt-1 space-y-1">
                  {rec.products.map((p, i) => <li key={i} className="text-brown-black font-medium" data-testid={`verification-product-${i}`}>{p}</li>)}
                </ul>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.12em] text-brown-dark/50 font-semibold">Date d'achat</p>
                <p className="text-brown-black mt-1" data-testid="verification-date">{new Date(rec.purchase_date).toLocaleString("fr-FR")}</p>
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-gold/15">
                <img src={`${BACKEND}/api/verification/${rec.digital_id}/qr`} alt="QR de vérification" className="w-24 h-24 rounded-lg border border-gold/20" data-testid="verification-qr" />
                <p className="flex items-center gap-2 text-sm text-brown-black/60">
                  <ShieldCheck size={16} className="text-gold" /> Authenticité certifiée par le Ministère Prophète Steeve Orphôss.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationPage;
